import { ref, computed, onMounted, onUnmounted, Ref } from 'vue';
import { useCollectionsStore } from '@/store/useCollectionsStore';
import { storeToRefs } from 'pinia';
import type { CollectionItemInterface } from '@/store/useCollectionsStore';

export interface UseCollectionPickerOptions {
  selectedId?: Ref<string | null> | string | null;
  excludeSecure?: boolean;
  excludeOnboarding?: boolean;
  onSelect?: (collection: CollectionItemInterface) => void;
  onCancel?: () => void;
}

export const useCollectionPicker = (options: UseCollectionPickerOptions = {}) => {
  const { selectedId = null, excludeSecure = true, excludeOnboarding = true, onSelect, onCancel } = options;

  const collectionsStore = useCollectionsStore();
  const { isLoading, list: collections } = storeToRefs(collectionsStore);

  const loadMoreTrigger = ref<HTMLElement | null>(null);
  const hasLoadedInitial = ref(false);

  // Get the actual selected ID value (handle both ref and plain value)
  const getSelectedId = (): string | null => {
    if (selectedId && typeof selectedId === 'object' && 'value' in selectedId) {
      return selectedId.value;
    }
    return selectedId as string | null;
  };

  // Use store's getter for filtered collections
  const filteredCollections = computed(() => {
    return collectionsStore.getPickerCollections({
      excludeSecure,
      excludeOnboarding,
    });
  });

  const hasMore = computed(() => !collectionsStore.isLoadedAll);

  const isSelected = (collection: CollectionItemInterface): boolean => {
    return collection.id === getSelectedId();
  };

  const handleSelect = (collection: CollectionItemInterface) => {
    if (onSelect) {
      onSelect(collection);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const loadInitialCollections = async () => {
    if (!hasLoadedInitial.value) {
      try {
        await collectionsStore.load();
        hasLoadedInitial.value = true;
      } catch (error) {
        console.error('[useCollectionPicker] Failed to load initial collections:', error);
        hasLoadedInitial.value = true; // Mark as attempted even if failed
      }
    }
  };

  const loadMoreCollections = async () => {
    if (!hasMore.value) {
      return;
    }
    await collectionsStore.loadMore();
  };

  // Intersection Observer for infinite scroll
  let observer: IntersectionObserver | null = null;

  const setupInfiniteScroll = () => {
    if (!loadMoreTrigger.value) {
      return;
    }

    observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore.value) {
          loadMoreCollections();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreTrigger.value);
  };

  const cleanupInfiniteScroll = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };

  onMounted(async () => {
    await loadInitialCollections();
    setupInfiniteScroll();
  });

  onUnmounted(() => {
    cleanupInfiniteScroll();
  });

  return {
    // Refs from store
    isLoading,
    collections,

    // Template refs
    loadMoreTrigger,

    // Computed
    filteredCollections,
    hasMore,

    // Methods
    isSelected,
    handleSelect,
    handleCancel,
    loadInitialCollections,
    loadMoreCollections,
  };
};
